import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    StyledLabel,
    CheckboxIcon,
    StyledSelectInput,
} from "@aureskonnect/react-ui";
import NumericInput from "react-numeric-input";
import { toast } from "react-toastify";
import { AvGroup } from "availity-reactstrap-validation";
import classnames from "classnames";
import { useSnapshot } from "valtio";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";
import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import {
    store as device,
    setIsActionsClicked,
    setActions,
    setIsEdited,
    setIsPlusIconClicked,
} from "./store";
import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import {
    checkOverlap,
    createDataZoneAmount,
    extractTranchesFromGroup,
    formatZoneAmountLivraisonData,
    groupByZone,
    INITIAL_VALIDATION,
    INITIAL_ZONE_AMOUNT_FORM_DATA,
} from "./hooks";
import {
    Tranche,
    SelectOption,
    ZoneRecord,
    getShopVatList,
    isInvalidDistanceRange,
    resolveDefaultTva,
    resolveTvaFromList,
    scrollToFirstError,
} from "../shared";
import { PostCity } from "../City/hooks";
import ErrorToast from "@components/Common/ErrorTost";
import { setOrders, store as shopStore } from "../../store";
import { useFetchGlobal } from "../../../../../../api/hooks";
import { store as storeGlobal } from "@components/VerticalLayout/store";
import ErrorMessage from "../ErrorMessage";
import FormLayout from "../FormLayout";
import TranchesManager, { rebuildTranches } from "../TranchesManager";

type ZoneAmountFormType = {
    isAddZoneAmount: boolean;
    setDataIsUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;
    selectedRows: Record<string, unknown>[];
    setLocalFilterActive: (v: boolean) => void;
    setIsAddZoneAmount: (v: boolean) => void;
    isAddNewDeviseButtonClicked: boolean;
    setSelectedRows: (v: Record<string, unknown>[]) => void;
    selectedRowShops: Record<string, unknown>[];
    ZoneData: { data: ZoneRecord[] };
};

export default function ZoneAmountForm({
    setIsAddZoneAmount,
    isAddZoneAmount,
    setDataIsUpdated,
    selectedRows,
    setLocalFilterActive,
    isAddNewDeviseButtonClicked,
    setSelectedRows,
    selectedRowShops,
    ZoneData,
}: ZoneAmountFormType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const [title, setTitle] = React.useState<string>("");
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const { franchiseID } = useSnapshot(storeGlobal);

    const { isActionsClicked, isEdited, isPlusIconClicked } = useSnapshot(
        device
    );
    const [
        isClickedBouttonValider,
        setIsClickedBouttonValider,
    ] = React.useState<boolean>(true);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [zoneFormData, setZoneFormData] = useState(
        INITIAL_ZONE_AMOUNT_FORM_DATA
    );
    const [validation, setValidation] = React.useState<
        typeof INITIAL_VALIDATION
    >(INITIAL_VALIDATION);
    const [tranches, setTranches] = useState<Tranche[]>([]);

    const listTva = useMemo(
        () =>
            getShopVatList(selectedRowShops).map((el) => ({
                label: el.value,
                value: el.id,
            })),
        [selectedRowShops]
    );
    const countryShop = useMemo(
        () => [
            {
                label: selectedRowShops[0]?.["Country shop"],
                value: selectedRowShops[0]?.["Country shop"],
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedRowShops[0]?.["Country shop"]]
    );
    const listDistanceZone = useMemo(
        () =>
            groupByZone(ZoneData.data).map((g) => ({
                end: g.finDistance,
                debut: g.debutDistance,
            })),
        [ZoneData.data]
    );

    const resetForm = useCallback(() => {
        setIsLoading(false);
        setValidation(INITIAL_VALIDATION);
        setZoneFormData(INITIAL_ZONE_AMOUNT_FORM_DATA);
        setTranches([]);
        setDataIsUpdated?.(true);
        setIsEdited(false);
        setLocalFilterActive(false);
        clearData();
        clearMultiCustomSelectionData();
        setIsClickedBouttonValider(true);
    }, [setDataIsUpdated, setLocalFilterActive]);

    const addZone = useCallback(async () => {
        if (
            !resolveTvaFromList(
                zoneFormData.tva,
                getShopVatList(selectedRowShops)
            )
        ) {
            ErrorToast(t("Invalid VAT rate"));
            setIsLoading(false);
            return;
        }
        let localStZoneLivraison = formatZoneAmountLivraisonData(ZoneData.data);
        setIsClickedBouttonValider(false);

        const payload = createDataZoneAmount(
            localStZoneLivraison,
            zoneFormData,
            tranches,
            selectedRowShops,
            selectedRows[0],
            franchiseID,
            false
        );

        try {
            const responseData = await PostCity(payload, fetchGlobal);
            if (responseData.error) {
                setDataIsUpdated!(true);
                throw Error("error");
            }

            const updatedDataPrice = {
                ...(shopStore.orders.dataPrice || {}),
                stZoneLivraisonMontant: payload.data,
            };

            setOrders({ dataPrice: updatedDataPrice });
            toast.success(`${t("Area added successfully")!}`, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            });

            resetForm();
        } catch (e) {
            setIsLoading(false);
            ErrorToast(`${t("There's an error")!}`);
        }
    }, [
        ZoneData.data,
        zoneFormData,
        tranches,
        selectedRowShops,
        selectedRows,
        franchiseID,
        t,
        resetForm,
        setDataIsUpdated,
        fetchGlobal,
    ]);

    const editZone = React.useCallback(async () => {
        if (
            !isEdited ||
            (validation.isDistanceEdited &&
                selectedRows.length > 0 &&
                selectedRows[0][t("remote Debut")] ===
                    zoneFormData.remoteDebut &&
                selectedRows[0][t("end Distance")] === zoneFormData.endDistance)
        ) {
            setIsModalOpened(true);
            return;
        }

        if (
            !resolveTvaFromList(
                zoneFormData.tva,
                getShopVatList(selectedRowShops)
            )
        ) {
            ErrorToast(t("Invalid VAT rate"));
            setIsLoading(false);
            return;
        }

        let localStZoneLivraison = formatZoneAmountLivraisonData(ZoneData.data);
        setIsClickedBouttonValider(false);

        const payload = createDataZoneAmount(
            localStZoneLivraison,
            zoneFormData,
            tranches,
            selectedRowShops,
            selectedRows[0],
            franchiseID,
            true
        );

        try {
            const responseData = await PostCity(payload, fetchGlobal);
            if (responseData.error) {
                setDataIsUpdated!(true);
                throw Error("error");
            }

            const updatedDataPrice = {
                ...(shopStore.orders.dataPrice || {}),
                stZoneLivraisonMontant: payload.data,
            };

            setOrders({ dataPrice: updatedDataPrice });
            toast.success(`${t("modification made successfully")!}`, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            });

            setSelectedRows([]);
            setIsAddZoneAmount(!isAddZoneAmount);
            setIsPlusIconClicked(false);
            resetForm();
        } catch (e) {
            setIsLoading(false);
            ErrorToast(`${t("There's an error")!}`);
        }
    }, [
        ZoneData.data,
        zoneFormData,
        tranches,
        selectedRowShops,
        selectedRows,
        franchiseID,
        t,
        isEdited,
        validation.isDistanceEdited,
        resetForm,
        setDataIsUpdated,
        setIsModalOpened,
        setIsAddZoneAmount,
        isAddZoneAmount,
        setSelectedRows,
        fetchGlobal,
    ]);

    const updateValidation = useCallback((field: string, value: boolean) => {
        setValidation((prev) => ({
            ...prev,
            [field]: value,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTranchesChanged = useCallback(() => {
        setIsEdited(true);
        updateValidation("isTranchesEmpty", false);
    }, [updateValidation]);

    const updateField = (field: string, value: string | number) => {
        setZoneFormData((prev) => ({ ...prev, [field]: value }));
    };

    const isDuplicate =
        selectedRows.length > 0 && selectedRows[0]?.isDuplicate === true;

    const handleValidate = useCallback(() => {
        if (!zoneFormData.countryData) updateValidation("selectCountry", true);
        if (!zoneFormData.tva) updateValidation("inputTva", true);
        if (
            isInvalidDistanceRange(
                zoneFormData.remoteDebut,
                zoneFormData.endDistance
            )
        )
            updateValidation("isDistanceExist", true);
        if (tranches.length === 0) updateValidation("isTranchesEmpty", true);
        if (
            zoneFormData.countryData &&
            zoneFormData.tva &&
            !isInvalidDistanceRange(
                zoneFormData.remoteDebut,
                zoneFormData.endDistance
            ) &&
            !validation.isOverlap &&
            tranches.length > 0
        ) {
            setIsLoading(true);
            selectedRows.length === 0 || isDuplicate ? addZone() : editZone();
        } else {
            scrollToFirstError();
        }
    }, [
        zoneFormData,
        validation,
        tranches,
        selectedRows,
        addZone,
        editZone,
        updateValidation,
        isDuplicate,
    ]);

    const handleCancel = useCallback(() => {
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setSelectedRows([]);
                setIsAddZoneAmount(false);
                setIsEdited(false);
                clearData();
                clearMultiCustomSelectionData();
            });
        } else {
            setSelectedRows([]);
            setIsAddZoneAmount(false);
            setIsEdited(false);
            clearData();
            clearMultiCustomSelectionData();
        }
    }, [isEdited, isActionsClicked, setIsAddZoneAmount, setSelectedRows]);

    useEffect(() => {
        if (selectedRows.length > 0) {
            const row = selectedRows[0];
            const debut = Number(row[t("remote Debut")] || 0);
            const fin = Number(row[t("end Distance")] || 0);

            if (row.isDuplicate) {
                setTitle(t("Add a zone + amount"));
                updateValidation("isDistanceEdited", true);
            } else {
                setTitle(t("Modify a zone + amount"));
            }

            setZoneFormData({
                countryData: String(selectedRowShops[0]["Country shop"] || ""),
                remoteDebut: debut,
                endDistance: fin,
                tva: String(row["Tva"] || ""),
            });
            // Extraire les tranches depuis les enregistrements plats
            const tranchesFromStore = extractTranchesFromGroup(
                ZoneData.data,
                debut,
                fin
            );
            setTranches(rebuildTranches(tranchesFromStore));
        } else {
            setTitle(t("Add a zone + amount"));
            setZoneFormData({
                ...INITIAL_ZONE_AMOUNT_FORM_DATA,
                tva: resolveDefaultTva(getShopVatList(selectedRowShops)),
            });
            setTranches([]);
            setValidation(INITIAL_VALIDATION);
            setIsEdited(false);
            setIsClickedBouttonValider(true);
        }
        // eslint-disable-next-line
    }, [isAddNewDeviseButtonClicked, selectedRows, t, listTva]);

    React.useEffect(
        () => {
            if (isLoading) return;
            let localOverlap: boolean = checkOverlap(
                listDistanceZone,
                selectedRows,
                validation,
                zoneFormData,
                isDuplicate
            );
            updateValidation("isOverlap", localOverlap);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            zoneFormData.endDistance,
            zoneFormData.remoteDebut,
            selectedRows,
            listDistanceZone,
            validation.isDistanceEdited,
            isLoading,
            t,
        ]
    );

    React.useEffect(
        () => {
            if (isPlusIconClicked || selectedRows.length > 0) {
                updateValidation("selectCountry", false);
                updateValidation("inputTva", false);
                updateValidation("isDistanceExist", false);
                updateValidation("isTranchesEmpty", false);

                setIsPlusIconClicked(false);
            }
            if (
                zoneFormData.remoteDebut !== 0 ||
                zoneFormData.endDistance !== 0
            ) {
                updateValidation("isDistanceExist", false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            isPlusIconClicked,
            selectedRows,
            zoneFormData.remoteDebut,
            zoneFormData.endDistance,
        ]
    );

    return (
        <FormLayout
            title={title}
            onCancel={handleCancel}
            onValidate={handleValidate}
            isValidateDisabled={!isClickedBouttonValider}
            setDataIsUpdated={setDataIsUpdated}
            isModalOpened={isModalOpened}
            setIsModalOpened={setIsModalOpened}
            isAddClicked={isAddZoneAmount}
            setIsAddClicked={setIsAddZoneAmount}
            setEditedData={setSelectedRows}
        >
            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("Country")}
                </StyledLabel>
                <StyledSelectInput
                    data-testid="zone-amount-country-select"
                    isClearable
                    autoFocus
                    noOptionsMessage={() => t("No options")}
                    options={countryShop}
                    autocomplete="off"
                    className={classnames("", {
                        input__clz: validation.selectCountry,
                    })}
                    name="change"
                    onChange={(e: SelectOption | null) => {
                        updateField("countryData", e?.label || "");
                        updateValidation("selectCountry", false);
                        setIsEdited(true);
                        setIsClickedBouttonValider(true);
                    }}
                    value={
                        zoneFormData.countryData
                            ? {
                                  label: zoneFormData.countryData,
                                  value: zoneFormData.countryData,
                              }
                            : null
                    }
                    placeholder={`${t("Select")}…`}
                    type="text"
                />
                {validation.selectCountry && (
                    <ErrorMessage message={t("Please select a country")} />
                )}
            </AvGroup>

            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("Delivery area (in Km)")}
                </StyledLabel>
                <div className="d-flex justify-content-between ml-3">
                    <span
                        style={{
                            top: "499px",
                            left: "1304px",
                            height: "21px",
                            textAlign: "left",
                            font: "normal normal normal 16px/21px Segoe UI",
                            color: "#2B2828",
                            opacity: 1,
                        }}
                        className="px-1 mt-2"
                    >
                        {t("Of")}
                    </span>
                    <NumericInput
                        data-testid="zone-amount-start-distance"
                        className={classnames("form-control-input-zone__clz", {
                            input__clz:
                                validation.isDistanceExist ||
                                validation.isOverlap,
                        })}
                        size={25}
                        min={0}
                        max={5000}
                        step={1}
                        onChange={(num: number | null) => {
                            if (num !== null) {
                                updateField("remoteDebut", num || 0);
                                setIsEdited(true);
                                updateValidation("isDistanceExist", true);
                                updateValidation("isDistanceEdited", true);
                            }
                        }}
                        value={zoneFormData.remoteDebut}
                    />
                    <span
                        className="ml-2 mt-2 px-1"
                        style={{
                            top: "499px",
                            left: "1304px",
                            width: "25px",
                            height: "21px",
                            textAlign: "left",
                            font: "normal normal normal 16px/21px Segoe UI",
                            color: "#2B2828",
                            opacity: 1,
                        }}
                    >
                        {t("at")}
                    </span>
                    <NumericInput
                        data-testid="zone-amount-end-distance"
                        className={classnames("form-control-input-zone__clz", {
                            input__clz:
                                validation.isDistanceExist ||
                                validation.isOverlap ||
                                (validation.isDistanceEdited &&
                                    isInvalidDistanceRange(
                                        zoneFormData.remoteDebut,
                                        zoneFormData.endDistance
                                    )),
                        })}
                        size={25}
                        min={0}
                        max={5000}
                        step={1}
                        onChange={(num: number | null) => {
                            updateField("endDistance", num || 0);
                            setIsEdited(true);
                            updateValidation("isDistanceEdited", true);
                        }}
                        value={zoneFormData.endDistance}
                    />
                </div>
                {(validation.isDistanceExist ||
                    (validation.isDistanceEdited &&
                        isInvalidDistanceRange(
                            zoneFormData.remoteDebut,
                            zoneFormData.endDistance
                        ))) && (
                    <ErrorMessage
                        message={t("Please enter a valid distance")}
                    />
                )}
                {validation.isOverlap && (
                    <ErrorMessage message={t("Overlap detected")} />
                )}
            </AvGroup>

            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("TVA")}
                </StyledLabel>
                <StyledSelectInput
                    data-testid="zone-amount-tva-select"
                    isClearable
                    noOptionsMessage={() => t("No options")}
                    options={listTva}
                    autocomplete="off"
                    className={classnames("", {
                        input__clz: validation.inputTva,
                    })}
                    name="tva"
                    onChange={(e: SelectOption | null) => {
                        updateField("tva", e?.label || "");
                        setIsEdited(true);
                        updateValidation("inputTva", false);
                        setIsClickedBouttonValider(true);
                    }}
                    value={
                        zoneFormData.tva
                            ? {
                                  label: zoneFormData.tva,
                                  value: zoneFormData.tva,
                              }
                            : null
                    }
                    placeholder={`${t("Select")}…`}
                    type="text"
                    maxMenuHeight="80px"
                />
                {validation.inputTva && (
                    <ErrorMessage message={t("Please select a vat rate")} />
                )}
            </AvGroup>

            {/* Tranches section */}
            <AvGroup>
                <StyledLabel htmlFor="text" className="required__clz mt-3">
                    {t("Order amount fee tiers")}
                </StyledLabel>
                <TranchesManager
                    tranches={tranches}
                    setTranches={setTranches}
                    onChanged={handleTranchesChanged}
                    validationError={validation.isTranchesEmpty}
                />
            </AvGroup>
        </FormLayout>
    );
}
